import { useCallback, useEffect, useRef, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { errorText, previewRow, splitOptions, type BuilderField, type PreviewResult } from './builderApi';

type Values = Record<string, string | null>;
interface PreviewRowState { id: number; values: Values; result: PreviewResult['evaluation'] | null }

const EVAL_DELAY_MS = 250;
const PILL: Record<string, string> = { Pass: 'ok', Fail: 'alert', Incomplete: 'warn' };

interface Props { fields: BuilderField[]; templateName: string }

/**
 * Logbook Preview (prototype ALPACA_Daily_QC_Setup_v20.html): the logbook laid out from the
 * CURRENT (even unsaved) configuration — one column per field (frozen ones pinned), type test
 * values to check the calculations and pass/fail rules, which the API evaluates. Nothing is saved.
 */
export default function BuilderPreview({ fields, templateName }: Props) {
  const notify = useNotify();
  const [rows, setRows] = useState<PreviewRowState[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const evaluate = useCallback((id: number, values: Values) => {
    const old = timers.current.get(id);
    if (old) clearTimeout(old);
    timers.current.set(id, setTimeout(() => {
      previewRow(fields, values)
        .then((r) => setRows((prev) => prev.map((x) => (x.id === id ? { ...x, result: r.evaluation } : x))))
        .catch((e) => notify.error(errorText(e, 'Could not evaluate the preview row.')));
    }, EVAL_DELAY_MS));
  }, [fields, notify]);

  /** A blank row with the fields' defaults ('today' → today), evaluated at once. */
  const blankRow = useCallback(async (): Promise<PreviewRowState | null> => {
    try {
      const r = await previewRow(fields, null);
      return { id: nextId.current++, values: { ...r.defaults }, result: r.evaluation };
    } catch (e) {
      notify.error(errorText(e, 'Could not build the preview.'));
      return null;
    }
  }, [fields, notify]);

  const reset = useCallback(async () => {
    const r = await blankRow();
    setRows(r ? [r] : []);
  }, [blankRow]);

  // A fresh preview whenever the configuration changes (the prototype re-renders from it).
  useEffect(() => { if (fields.length) void reset(); else setRows([]); }, [fields, reset]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  const addRow = async () => {
    const r = await blankRow();
    if (r) setRows((prev) => [r, ...prev]);
  };

  const setValue = (row: PreviewRowState, name: string, v: string | null) => {
    const values = { ...row.values, [name]: v };
    setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, values } : x)));
    evaluate(row.id, values);
  };

  const cell = (f: BuilderField, row: PreviewRowState) => {
    const v = row.values[f.name] ?? '';
    if (f.calc) return <span className="qcb-calc">{row.result?.calc[f.name] ?? ''}</span>;
    switch (f.type) {
      case 'Date': return <input className="qcb-pin" type="date" value={v} onChange={(e) => setValue(row, f.name, e.target.value)} />;
      case 'Time': return <input className="qcb-pin" type="time" value={v} onChange={(e) => setValue(row, f.name, e.target.value)} />;
      case 'DateTime': return <input className="qcb-pin" type="datetime-local" value={v} onChange={(e) => setValue(row, f.name, e.target.value)} />;
      case 'Numeric': return <input className="qcb-pin qcb-num" type="number" step="any" value={v} onChange={(e) => setValue(row, f.name, e.target.value)} />;
      case 'Checkbox': return (
        <input type="checkbox" aria-label={f.caption ?? f.name} checked={v === '1'}
          onChange={(e) => setValue(row, f.name, e.target.checked ? '1' : '0')} />);
      case 'AutoNumber': return <span className="qcb-calc" title="Generated on save">{`${f.options ?? ''}yyMMddNN`}</span>;
      case 'ComboBox': {
        const opts = splitOptions(f.options);
        return (
          <select className="qcb-pin" value={v} onChange={(e) => setValue(row, f.name, e.target.value || null)}>
            <option value="" />{opts.map((o) => <option key={o}>{o}</option>)}
          </select>);
      }
      case 'Attachment': return <button type="button" className="ca-btn qcb-attach" disabled>📎 Attach</button>;
      default: return <input className="qcb-pin" value={v} onChange={(e) => setValue(row, f.name, e.target.value)} />;
    }
  };

  let sticky = 0;
  while (sticky < fields.length && fields[sticky]!.freeze) sticky++;

  const columns: AppColumn<PreviewRowState>[] = [
    ...fields.map<AppColumn<PreviewRowState>>((f) => ({
      field: `f_${f.name}`,
      header: (<span title={`${f.caption ?? f.name}${f.accept ? ` · pass when ${f.accept}` : ''}`}>
        {f.caption || f.name}{f.symbol && <span className="qcb-sym-status">{f.symbol}</span>}</span>),
      headerText: f.caption || f.name,
      width: f.width || 100, sortable: false, filterable: false, noExport: true,
      render: (row) => {
        const a = row.result?.accept[f.name];
        return <div className={`qcb-pcell${a === true ? ' ok' : a === false ? ' bad' : ''}`}
          title={f.accept ? `Pass when ${f.accept}${a === false ? ' — FAILED' : ''}` : undefined}>{cell(f, row)}</div>;
      },
    })),
    { field: 'status', header: 'Status', width: 90, align: 'center', sortable: false, filterable: false, noExport: true,
      render: (row) => row.result ? <span className={`adg-pill ${PILL[row.result.status] ?? 'info'}`}>{row.result.status}</span> : null },
    { field: 'del', header: '', width: 32, align: 'center', sortable: false, filterable: false, noExport: true,
      render: (row) => (<button type="button" className="qcb-delrow" title="Remove row" aria-label="Remove row"
        onClick={() => setRows((prev) => prev.filter((x) => x.id !== row.id))}>×</button>) },
  ];

  return (
    <div>
      <div className="qcb-toolbar">
        <button type="button" className="ca-btn" onClick={() => void addRow()} disabled={!fields.length}>＋ Add Row</button>
        <button type="button" className="ca-btn" onClick={() => void reset()} disabled={!fields.length}>↻ Reset Preview</button>
        <span className="qcb-muted">Layout preview of the logbook. Type test values to check formulas and pass/fail rules — nothing is saved.</span>
      </div>
      <div className="qcb-section">
        <div className="qcb-sectitle">Logbook Preview — {templateName.trim() || 'Untitled'}
          <span className="qcb-note">{fields.length} configured fields, {fields.filter((f) => f.accept).length} acceptance rules</span>
        </div>
        {fields.length === 0
          ? <div className="qcb-empty">No fields configured. Add fields on the Builder tab to see the logbook.</div>
          : (
            <div className="bl-card">
              <AppDataGrid<PreviewRowState> rows={rows} columns={columns} getRowId={(r) => r.id}
                stickyColumns={sticky} defaultPageSize={1000} hideHeaderTools maximizable={false} autoFocusFirstRow={false} hideFooter
                emptyText="No rows. Use Add Row to test the layout." />
            </div>
          )}
      </div>
    </div>
  );
}
